import DOMPurify from 'dompurify';

type PresignedUploadResponse = {
  uploadUrl: string;
  fileUrl: string;
  key?: string;
  bucket?: string;
};

export default class ImageUploader {
    static allowedTypes = [
        'image/png',
        'image/svg',
        'image/svg+xml',
    ];

    public async uploadFile(
        file: File,
        onUpload: (url: string) => void,
        onError: (error: string) => void
        ): Promise<void> {
        try {
            if (!ImageUploader.allowedTypes.includes(file.type)) {
            throw new Error('Unsupported file type.');
            }

            let processedFile: File = file;

            if (file.type === 'image/svg+xml' || file.type === 'image/svg') {
                const sanitizedSvg = await this.sanitizeSVG(file);
                processedFile = this.createSanitizedFile(sanitizedSvg, file);
            }

            const presignedData = await this.getPresignedUploadUrl(processedFile);

            await this.uploadToS3(presignedData.uploadUrl, processedFile);

            onUpload(presignedData.fileUrl);
        } catch (error: any) {
            console.error('ImageUploader - Error:', error);
            onError(error.message || 'An unknown error occurred during upload.');
        }
    }

    private async getPresignedUploadUrl(file: File): Promise<PresignedUploadResponse> {
        const payload = {
            fileName: file.name,
            contentType: file.type,
            brand: 'raven',
        };

        const fn = window['get_presigned_upload_url'];

        if (typeof fn !== 'function') {
            throw new Error('window.get_presigned_upload_url is not available');
        }

        const result = await fn(payload);

        const data = result?.data ?? result;

        if (!data?.uploadUrl || !data?.fileUrl) {
            throw new Error(
            result?.data?.message ||
            result?.message ||
            'Invalid presigned URL response.'
            );
        }

        return data as PresignedUploadResponse;
    }

  private async uploadToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed. Status: ${response.status}`);
    }
  }

  private sanitizeSVG(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const svgContent = reader.result as string;
          const sanitizedSvg = DOMPurify.sanitize(svgContent, {
            USE_PROFILES: { svg: true },
          });

          const parser = new DOMParser();
          const parsedSvg = parser.parseFromString(sanitizedSvg, 'image/svg+xml');
          const parserError = parsedSvg.querySelector('parsererror');

          if (parserError) {
            throw new Error('Invalid SVG file.');
          }

          resolve(sanitizedSvg);
        } catch (err: any) {
          reject(err);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read the SVG file.'));
      };

      reader.readAsText(file);
    });
  }

  private createSanitizedFile(sanitizedSvg: string, originalFile: File): File {
    return new File([sanitizedSvg], originalFile.name, { type: originalFile.type });
  }
}